import React, { useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { playerPosition } from "./controls";
import { playGazelleSound } from "./audio";

interface GazelleProps {
  position: [number, number, number];
  rotationY?: number;
}

const SOUND_RADIUS = 8;
const WANDER_RADIUS = 3.2;
const COLOR = "#c9a876";

/** A slender grazing gazelle — wanders a patch, more alert/quicker than the horse. */
const Gazelle: React.FC<GazelleProps> = ({ position, rotationY = 0 }) => {
  const groupRef = useRef<THREE.Group>(null);
  const neckRef = useRef<THREE.Group>(null);
  const soundTimer = useRef(1.5 + Math.random() * 4);
  const grazeOffset = useRef(Math.random() * 10);
  const wanderPhase = useRef(rotationY + Math.random() * Math.PI * 2);
  const wanderSpeed = useRef(0.1 + Math.random() * 0.08);
  const worldPos = useRef(new THREE.Vector3(...position));

  useFrame(({ clock }, delta) => {
    const t = clock.getElapsedTime() + grazeOffset.current;
    const graze = Math.max(0, Math.sin(t * 1.1)) ** 0.6;
    if (neckRef.current) neckRef.current.rotation.x = graze * 0.85;

    const wt = clock.getElapsedTime() * wanderSpeed.current + wanderPhase.current;
    const x = position[0] + Math.sin(wt) * WANDER_RADIUS;
    const z = position[2] + Math.cos(wt * 0.7) * WANDER_RADIUS * 0.8;
    if (groupRef.current) {
      groupRef.current.position.set(x, position[1], z);
      const dx = Math.cos(wt) * WANDER_RADIUS;
      const dz = -Math.sin(wt * 0.7) * WANDER_RADIUS * 0.8 * 0.7;
      groupRef.current.rotation.y = Math.atan2(-dx, -dz);
      worldPos.current.set(x, position[1], z);
    }

    soundTimer.current -= delta;
    if (soundTimer.current <= 0) {
      soundTimer.current = 5 + Math.random() * 7;
      if (playerPosition.distanceTo(worldPos.current) < SOUND_RADIUS) playGazelleSound();
    }
  });

  return (
    <group ref={groupRef} position={position} rotation={[0, rotationY, 0]} scale={0.7}>
      {/* Body */}
      <mesh position={[0, 0.85, 0]} castShadow>
        <boxGeometry args={[0.42, 0.42, 1.0]} />
        <meshStandardMaterial color={COLOR} roughness={0.9} />
      </mesh>
      {/* Neck + head */}
      <group ref={neckRef} position={[0, 1.1, -0.5]}>
        <mesh position={[0, 0.22, -0.08]} castShadow>
          <boxGeometry args={[0.2, 0.42, 0.2]} />
          <meshStandardMaterial color={COLOR} roughness={0.9} />
        </mesh>
        <mesh position={[0, 0.42, -0.24]} castShadow>
          <boxGeometry args={[0.16, 0.18, 0.3]} />
          <meshStandardMaterial color={COLOR} roughness={0.9} />
        </mesh>
        {/* Horns */}
        <mesh position={[-0.06, 0.62, -0.24]} rotation={[0.2, 0, 0.1]}>
          <boxGeometry args={[0.04, 0.28, 0.04]} />
          <meshStandardMaterial color="#3a2f22" roughness={0.8} />
        </mesh>
        <mesh position={[0.06, 0.62, -0.24]} rotation={[0.2, 0, -0.1]}>
          <boxGeometry args={[0.04, 0.28, 0.04]} />
          <meshStandardMaterial color="#3a2f22" roughness={0.8} />
        </mesh>
      </group>
      {/* Legs — thin, built for speed */}
      {[
        [-0.16, 0.35, 0.35],
        [0.16, 0.35, 0.35],
        [-0.16, 0.35, -0.35],
        [0.16, 0.35, -0.35],
      ].map((p, i) => (
        <mesh key={i} position={p as [number, number, number]} castShadow>
          <boxGeometry args={[0.1, 0.7, 0.1]} />
          <meshStandardMaterial color="#7a5c3a" roughness={0.9} />
        </mesh>
      ))}
      {/* Tail */}
      <mesh position={[0, 0.85, 0.52]}>
        <boxGeometry args={[0.06, 0.3, 0.06]} />
        <meshStandardMaterial color="#3a2f22" roughness={0.9} />
      </mesh>
    </group>
  );
};

export default Gazelle;
